<template>
  <teleport to="body">
    <div v-if="chatStore.showNewConversationDialog" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="chatStore.showNewConversationDialog = false"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-2xl overflow-hidden w-[480px] max-h-[600px] flex flex-col">
          <!-- 头部 -->
          <div class="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
            <h3 class="text-base font-semibold text-slate-900">
              {{ mode === 'chat' ? '新建会话' : '创建群聊' }}
            </h3>
            <div class="flex items-center gap-2">
              <button
                v-if="mode === 'chat'"
                class="text-xs text-primary-600 hover:text-primary-700 px-2.5 py-1 rounded-md hover:bg-primary-50 transition-colors cursor-pointer"
                @click="mode = 'group'"
              >
                创建群聊
              </button>
              <button
                v-else
                class="text-xs text-primary-600 hover:text-primary-700 px-2.5 py-1 rounded-md hover:bg-primary-50 transition-colors cursor-pointer"
                @click="mode = 'chat'; selectedMembers = []"
              >
                新建单聊
              </button>
              <button
                class="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                @click="chatStore.showNewConversationDialog = false"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- 群聊信息（创建群聊模式） -->
          <div v-if="mode === 'group'" class="px-5 py-3 border-b border-slate-100 flex-shrink-0">
            <input
              v-model="groupName"
              type="text"
              placeholder="输入群聊名称..."
              class="form-input mb-2"
            />
            <input
              v-model="groupDescription"
              type="text"
              placeholder="群聊描述（可选）"
              class="form-input"
            />
            <!-- 已选成员 -->
            <div v-if="selectedMembers.length > 0" class="flex items-center flex-wrap gap-1.5 mt-2">
              <span
                v-for="member in selectedMembers"
                :key="member.id"
                class="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary-50 text-primary-700 rounded-full"
              >
                {{ member.name }}
                <button
                  class="p-0.5 hover:bg-primary-100 rounded-full cursor-pointer"
                  @click="toggleMember(member)"
                >
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </span>
            </div>
          </div>

          <!-- 搜索 -->
          <div class="px-5 py-3 border-b border-slate-100 flex-shrink-0">
            <div class="relative">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input
                v-model="contactSearch"
                type="text"
                placeholder="搜索联系人..."
                class="w-full pl-10 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <!-- 分类标签 -->
            <div class="flex items-center gap-1 mt-2">
              <button
                v-for="t in contactTabs"
                :key="t.value"
                class="px-2.5 py-1 text-xs rounded-md transition-colors cursor-pointer"
                :class="contactTab === t.value ? 'bg-primary-50 text-primary-600' : 'text-slate-500 hover:bg-slate-50'"
                @click="contactTab = t.value"
              >{{ t.label }}</button>
            </div>
          </div>

          <!-- 联系人列表 -->
          <div class="flex-1 overflow-y-auto">
            <div v-if="filteredContacts.length === 0" class="p-8 text-center">
              <p class="text-sm text-slate-400">没有找到匹配的联系人</p>
            </div>
            <div
              v-for="contact in filteredContacts"
              :key="contact.id"
              class="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors"
              @click="mode === 'chat' ? handleSelectContact(contact) : toggleMember(contact)"
            >
              <!-- 群聊模式复选框 -->
              <div v-if="mode === 'group'" class="flex-shrink-0">
                <div
                  class="w-4 h-4 rounded border-2 flex items-center justify-center transition-colors"
                  :class="isMemberSelected(contact) ? 'bg-primary-500 border-primary-500' : 'border-slate-300'"
                >
                  <svg v-if="isMemberSelected(contact)" class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
              </div>

              <div
                class="w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0"
                :class="contact.type === 'internal' ? 'bg-primary-500' : 'bg-emerald-500'"
              >
                {{ contact.name.slice(-2) }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1.5">
                  <span class="text-sm font-medium text-slate-700 truncate">{{ contact.name }}</span>
                  <span
                    class="px-1 py-0.5 text-[10px] leading-none rounded flex-shrink-0"
                    :class="contact.type === 'internal' ? 'bg-primary-100 text-primary-600' : 'bg-emerald-100 text-emerald-600'"
                  >{{ contact.type === 'internal' ? '内部' : '客户' }}</span>
                </div>
                <p class="text-[11px] text-slate-400 truncate">
                  {{ contact.department ? `${contact.department} · ${contact.position}` : contact.source || contact.phone }}
                </p>
              </div>
            </div>
          </div>

          <!-- 底部操作（创建群聊模式） -->
          <div v-if="mode === 'group'" class="px-5 py-3 border-t border-slate-200 flex items-center justify-between flex-shrink-0">
            <p class="text-xs text-slate-500">已选 {{ selectedMembers.length }} 人</p>
            <button
              class="px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer"
              :class="canCreateGroup ? 'bg-primary-500 text-white hover:bg-primary-600' : 'bg-slate-100 text-slate-400 cursor-not-allowed'"
              :disabled="!canCreateGroup"
              @click="handleCreateGroup"
            >
              创建群聊
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useChatStore } from '@/stores/chat'

const chatStore = useChatStore()

const mode = ref('chat') // chat | group
const contactSearch = ref('')
const contactTab = ref('all')
const groupName = ref('')
const groupDescription = ref('')
const selectedMembers = ref([])

const contactTabs = [
  { value: 'all', label: '全部' },
  { value: 'internal', label: '内部人员' },
  { value: 'customer', label: '客户' }
]

const filteredContacts = computed(() => {
  let result = chatStore.availableContacts
  if (contactTab.value !== 'all') {
    result = result.filter(c => c.type === contactTab.value)
  }
  if (contactSearch.value) {
    const keyword = contactSearch.value.toLowerCase()
    result = result.filter(c =>
      c.name.toLowerCase().includes(keyword) ||
      (c.department && c.department.toLowerCase().includes(keyword)) ||
      (c.phone && c.phone.includes(keyword))
    )
  }
  return result
})

const canCreateGroup = computed(() => {
  return groupName.value.trim() && selectedMembers.value.length >= 2
})

function handleSelectContact(contact) {
  chatStore.createNewConversation(contact)
}

function toggleMember(contact) {
  const index = selectedMembers.value.findIndex(m => m.id === contact.id)
  if (index > -1) {
    selectedMembers.value.splice(index, 1)
  } else {
    selectedMembers.value.push(contact)
  }
}

function isMemberSelected(contact) {
  return selectedMembers.value.some(m => m.id === contact.id)
}

function handleCreateGroup() {
  if (!canCreateGroup.value) return
  chatStore.createNewGroup({
    name: groupName.value.trim(),
    description: groupDescription.value.trim(),
    members: selectedMembers.value
  })
  // 重置状态
  mode.value = 'chat'
  groupName.value = ''
  groupDescription.value = ''
  selectedMembers.value = []
  contactSearch.value = ''
}
</script>
